import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatCZK, formatDateTime, formatQty } from "@/components/Shared";
import { toast } from "sonner";
import { PackagePlus, Plus, Check, ChevronsUpDown, X as XIcon, Search } from "lucide-react";
import SearchBar, { applyTextFilter } from "@/components/SearchBar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const UNITS = ["ks", "kg", "g", "l", "ml"];

// --- Searchable combobox for materials ---
function MaterialCombobox({ value, onChange, materials, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = materials.find((m) => m.id === value);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return materials;
    return materials.filter((m) => m.name.toLowerCase().includes(q));
  }, [q, materials]);

  const exactMatch = filtered.some((m) => m.name.toLowerCase() === q);

  const handleSelect = (m) => {
    onChange(m.id);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="material-combobox-trigger"
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          setTimeout(() => inputRef.current?.focus(), 30);
        }}
        className="input-cafe w-full text-left flex items-center justify-between gap-2"
      >
        <span className={`truncate ${selected ? "" : "text-[#9CA39C]"}`}>
          {selected ? `${selected.name} (${selected.unit})` : "Vyberte nebo hledejte…"}
        </span>
        <ChevronsUpDown className="w-4 h-4 text-[#57534E] shrink-0" />
      </button>
      {open && (
        <div
          data-testid="material-combobox-panel"
          className="absolute z-50 mt-1 w-full min-w-[260px] bg-white border border-[#E5E1D8] rounded-lg shadow-lg overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E1D8]">
            <Search className="w-4 h-4 text-[#57534E]" />
            <input
              ref={inputRef}
              data-testid="material-combobox-search"
              type="text"
              placeholder="Hledat surovinu…"
              className="flex-1 outline-none text-sm bg-transparent"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length > 0) {
                  e.preventDefault();
                  handleSelect(filtered[0]);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="p-1 rounded hover:bg-[#F9F6F0]"
              >
                <XIcon className="w-3.5 h-3.5 text-[#57534E]" />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-sm text-[#57534E] text-center">
                Žádná surovina nenalezena
              </li>
            )}
            {filtered.map((m) => {
              const isSel = m.id === value;
              return (
                <li key={m.id}>
                  <button
                    data-testid={`material-option-${m.id}`}
                    type="button"
                    onClick={() => handleSelect(m)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-[#F9F6F0] ${
                      isSel ? "bg-[#F9F6F0]" : ""
                    }`}
                  >
                    <span>
                      {m.name} <span className="text-[#57534E]">({m.unit})</span>
                    </span>
                    {isSel && <Check className="w-4 h-4 text-[#376A4D]" />}
                  </button>
                </li>
              );
            })}
            {q && !exactMatch && (
              <li className="border-t border-[#E5E1D8]">
                <button
                  data-testid="material-create-new"
                  type="button"
                  onClick={() => {
                    onCreateNew(query.trim());
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2.5 text-sm text-[#3A2D27] hover:bg-[#F9F6F0] flex items-center gap-2 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Vytvořit „{query.trim()}"
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function StockReceipts() {
  const [warehouses, setWarehouses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState({ material_id: "", warehouse_id: "", quantity: "", total_price: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  // Quick add material dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", unit: "ks", notes: "" });
  const [adding, setAdding] = useState(false);

  const matWh = warehouses.find((w) => w.type === "material");

  const load = async () => {
    const [w, m] = await Promise.all([api.get("/warehouses"), api.get("/materials")]);
    setWarehouses(w.data);
    setMaterials(m.data);
    const wh = w.data.find((x) => x.type === "material");
    if (wh) {
      const r = await api.get("/stock/batches", { params: { warehouse_id: wh.id } });
      setBatches(r.data);
      setForm((f) => ({ ...f, warehouse_id: wh.id }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.material_id || !form.warehouse_id || !form.quantity || !form.total_price) {
      toast.error("Vyplňte všechna pole");
      return;
    }
    const qty = parseFloat(form.quantity);
    const total = parseFloat(form.total_price);
    if (qty <= 0 || total <= 0) {
      toast.error("Hodnoty musí být větší než 0");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/stock/receipts", {
        material_id: form.material_id,
        warehouse_id: form.warehouse_id,
        quantity: qty,
        unit_price: total / qty,
        note: form.note,
      });
      toast.success(`Příjem zaznamenán — ${(total / qty).toFixed(2)} Kč/${matUnit(form.material_id) || "j"}`);
      setForm((f) => ({ ...f, material_id: "", quantity: "", total_price: "", note: "" }));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Chyba");
    } finally {
      setSubmitting(false);
    }
  };

  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matUnit = (id) => materials.find((m) => m.id === id)?.unit || "";

  const openAddDialog = (prefillName = "") => {
    setAddForm({ name: prefillName, unit: "ks", notes: "" });
    setAddOpen(true);
  };

  const submitNewMaterial = async () => {
    if (!addForm.name.trim()) {
      toast.error("Zadejte název suroviny");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/materials", {
        name: addForm.name.trim(),
        unit: addForm.unit,
        notes: addForm.notes || "",
      });
      setMaterials((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name, "cs")));
      setForm((f) => ({ ...f, material_id: data.id }));
      toast.success(`Surovina „${data.name}" přidána`);
      setAddOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Chyba");
    } finally {
      setAdding(false);
    }
  };

  const [search, setSearch] = useState("");
  const [filterMat, setFilterMat] = useState("");
  const filteredBatches = useMemo(() => {
    let list = batches.slice().reverse();
    if (filterMat) list = list.filter((b) => b.material_id === filterMat);
    return applyTextFilter(list, search, (b) => [b.batch_code, matName(b.material_id), b.note]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches, search, filterMat, materials]);

  return (
    <div data-testid="receipts-page">
      <PageHeader
        eyebrow="Materiálový sklad"
        title="Příjem surovin"
        description="Při každém příjmu se automaticky zakládá nová datumová šarže s vlastní nákupní cenou. Více příjmů stejné suroviny za den je v pořádku."
      />

      <div className="space-y-6">
        <form
          onSubmit={submit}
          className="card-cafe p-6 space-y-4"
          data-testid="receipt-form"
        >
          <div className="flex items-center gap-2 mb-2">
            <PackagePlus className="w-5 h-5 text-[#3A2D27]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
              Nový příjem
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            <Field label="Sklad">
              <select
                data-testid="receipt-warehouse"
                className="input-cafe w-full"
                value={form.warehouse_id}
                onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
              >
                {warehouses
                  .filter((w) => w.type === "material")
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
              </select>
            </Field>

            <div className="lg:col-span-2">
              <Field label="Surovina">
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <MaterialCombobox
                    value={form.material_id}
                    onChange={(id) => setForm({ ...form, material_id: id })}
                    materials={materials}
                    onCreateNew={(name) => openAddDialog(name)}
                  />
                </div>
                <button
                  data-testid="receipt-quick-add-material"
                  type="button"
                  onClick={() => openAddDialog("")}
                  className="shrink-0 h-[42px] w-[42px] grid place-items-center rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] text-[#3A2D27]"
                  title="Přidat novou surovinu"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </Field>
            </div>

            <Field label={`Množství (${matUnit(form.material_id) || "j."})`}>
              <input
                data-testid="receipt-qty"
                type="number"
                inputMode="decimal"
                step="0.001"
                min="0"
                className="input-cafe w-full"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                placeholder="např. 25"
              />
            </Field>

            <Field label="Celková cena (Kč)">
              <input
                data-testid="receipt-total-price"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                className="input-cafe w-full"
                value={form.total_price}
                onChange={(e) => setForm({ ...form, total_price: e.target.value })}
                placeholder="např. 500"
              />
              {form.quantity && form.total_price && parseFloat(form.quantity) > 0 && (
                <div className="text-xs text-[#57534E] mt-1 mono">
                  ≈ {(parseFloat(form.total_price) / parseFloat(form.quantity)).toFixed(2)} Kč/{matUnit(form.material_id) || "j."}
                </div>
              )}
            </Field>

            <button
              data-testid="receipt-submit"
              type="submit"
              disabled={submitting || !matWh}
              className="btn-primary disabled:opacity-60 h-[42px]"
            >
              {submitting ? "Zapisuji…" : "Přijmout"}
            </button>
          </div>

          <Field label="Poznámka (nepovinné)">
            <input
              data-testid="receipt-note"
              className="input-cafe w-full"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="dodavatel, faktura…"
            />
          </Field>

          {materials.length === 0 && (
            <p className="text-xs text-[#57534E]">
              Zatím žádné suroviny. Použijte tlačítko <strong>+</strong> u pole „Surovina" pro vytvoření první.
            </p>
          )}
        </form>

        <div className="card-cafe p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow">Historie šarží ({filteredBatches.length})</div>
          </div>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Hledat podle suroviny, kódu šarže nebo poznámky…"
            testid="receipts-search"
          >
            <select
              data-testid="receipts-filter-material"
              className="input-cafe py-1.5"
              value={filterMat}
              onChange={(e) => setFilterMat(e.target.value)}
            >
              <option value="">Všechny suroviny</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </SearchBar>

          {filteredBatches.length === 0 ? (
            <div className="text-sm text-[#57534E] py-8 text-center">Bez výsledků.</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-[#E5E1D8]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left">
                    <th className="p-3 text-[#57534E] font-medium">Šarže</th>
                    <th className="p-3 text-[#57534E] font-medium">Surovina</th>
                    <th className="p-3 text-[#57534E] font-medium text-right">Příjem</th>
                    <th className="p-3 text-[#57534E] font-medium text-right">Cena/ks</th>
                    <th className="p-3 text-[#57534E] font-medium text-right">Hodnota</th>
                    <th className="p-3 text-[#57534E] font-medium">Čas</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.map((b) => (
                    <tr key={b.id} className="border-t border-[#E5E1D8]">
                      <td className="p-3 mono text-xs">{b.batch_code}</td>
                      <td className="p-3">{matName(b.material_id)}</td>
                      <td className="p-3 text-right">
                        {formatQty(b.original_quantity, matUnit(b.material_id))}
                      </td>
                      <td className="p-3 text-right">{formatCZK(b.unit_price)}</td>
                      <td className="p-3 text-right font-medium">
                        {formatCZK(b.original_quantity * b.unit_price)}
                      </td>
                      <td className="p-3 text-[#57534E]">{formatDateTime(b.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quick add material dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md bg-white border-[#E5E1D8]" data-testid="material-add-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Nová surovina</DialogTitle>
            <DialogDescription>
              Surovina bude okamžitě k dispozici pro tento i další příjmy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <Field label="Název">
              <input
                data-testid="material-add-name"
                autoFocus
                className="input-cafe w-full"
                value={addForm.name}
                onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="např. Mouka hladká"
              />
            </Field>
            <Field label="Jednotka">
              <select
                data-testid="material-add-unit"
                className="input-cafe w-full"
                value={addForm.unit}
                onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Poznámka (nepovinné)">
              <input
                data-testid="material-add-notes"
                className="input-cafe w-full"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder="dodavatel, varianta…"
              />
            </Field>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="material-add-submit"
              onClick={submitNewMaterial}
              disabled={adding || !addForm.name.trim()}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Plus className="w-4 h-4" />
              {adding ? "Ukládám…" : "Přidat surovinu"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-[#1C1917] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
